
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { NZTaxTreatment, TaxLiabilityItem, TaxBenefitItem } from "@/lib/types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, FileText, Calculator, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface NZTaxManagerProps {
  taxTreatments: NZTaxTreatment[];
  setTaxTreatments: React.Dispatch<React.SetStateAction<NZTaxTreatment[]>>;
  taxLiabilities: TaxLiabilityItem[];
  setTaxLiabilities: React.Dispatch<React.SetStateAction<TaxLiabilityItem[]>>;
  taxBenefits: TaxBenefitItem[];
  setTaxBenefits: React.Dispatch<React.SetStateAction<TaxBenefitItem[]>>;
}

export function NZTaxManager({
  taxTreatments,
  setTaxTreatments,
  taxLiabilities,
  setTaxLiabilities,
  taxBenefits,
  setTaxBenefits,
}: NZTaxManagerProps) {
  // New item states
  const [newTaxTreatment, setNewTaxTreatment] = useState<Partial<NZTaxTreatment>>({
    category: "exploration",
    deductible: false,
    depreciable: false,
  });
  const [newTaxLiability, setNewTaxLiability] = useState<Partial<TaxLiabilityItem>>({
    year: new Date().getFullYear(),
    taxRate: 28, // Default NZ corporate tax rate
  });
  const [newTaxBenefit, setNewTaxBenefit] = useState<Partial<TaxBenefitItem>>({
    type: "deduction",
    applicableYears: [new Date().getFullYear()],
  });

  // Dialog states
  const [treatmentDialogOpen, setTreatmentDialogOpen] = useState(false);
  const [liabilityDialogOpen, setLiabilityDialogOpen] = useState(false);
  const [benefitDialogOpen, setBenefitDialogOpen] = useState(false);

  // Handle adding new items
  const handleAddTaxTreatment = () => {
    if (newTaxTreatment.expenseType && newTaxTreatment.category) {
      const treatment: NZTaxTreatment = {
        id: `treatment-${Date.now()}`,
        category: newTaxTreatment.category as NZTaxTreatment["category"],
        expenseType: newTaxTreatment.expenseType,
        deductible: newTaxTreatment.deductible || false,
        depreciable: newTaxTreatment.depreciable || false,
        depreciationRate: newTaxTreatment.depreciationRate,
        applicableLegislation: newTaxTreatment.applicableLegislation,
        specialProvisions: newTaxTreatment.specialProvisions,
        notes: newTaxTreatment.notes,
      };

      setTaxTreatments([...taxTreatments, treatment]);
      setNewTaxTreatment({
        category: "exploration",
        deductible: false,
        depreciable: false,
      });
      setTreatmentDialogOpen(false);
    }
  };

  const handleAddTaxLiability = () => {
    if (newTaxLiability.year && newTaxLiability.taxRate !== undefined) {
      const liability: TaxLiabilityItem = {
        id: `liability-${Date.now()}`,
        year: newTaxLiability.year,
        taxableIncome: newTaxLiability.taxableIncome || 0,
        taxRate: newTaxLiability.taxRate,
        taxPayable: newTaxLiability.taxPayable || 0,
        taxCredits: newTaxLiability.taxCredits || 0,
        netTaxLiability: newTaxLiability.netTaxLiability || 0,
        notes: newTaxLiability.notes,
      };

      setTaxLiabilities([...taxLiabilities, liability]);
      setNewTaxLiability({
        year: new Date().getFullYear(),
        taxRate: 28,
      });
      setLiabilityDialogOpen(false);
    }
  };

  const handleAddTaxBenefit = () => {
    if (newTaxBenefit.name && newTaxBenefit.type) {
      const benefit: TaxBenefitItem = {
        id: `benefit-${Date.now()}`,
        name: newTaxBenefit.name,
        type: newTaxBenefit.type as TaxBenefitItem["type"],
        value: newTaxBenefit.value || 0,
        applicableYears: newTaxBenefit.applicableYears || [new Date().getFullYear()],
        legislationReference: newTaxBenefit.legislationReference || "",
        requirements: newTaxBenefit.requirements || "",
        notes: newTaxBenefit.notes,
      };

      setTaxBenefits([...taxBenefits, benefit]);
      setNewTaxBenefit({
        type: "deduction",
        applicableYears: [new Date().getFullYear()],
      });
      setBenefitDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">NZ Tax Management</h2>
          <p className="text-muted-foreground">
            Track and manage New Zealand tax obligations specific to mining and exploration
          </p>
        </div>
      </div>

      <Tabs defaultValue="treatments" className="w-full">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="treatments">Tax Treatments</TabsTrigger>
          <TabsTrigger value="liabilities">Tax Liabilities</TabsTrigger>
          <TabsTrigger value="benefits">Tax Benefits</TabsTrigger>
        </TabsList>

        {/* Tax Treatments Tab */}
        <TabsContent value="treatments">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Mining & Exploration Tax Treatments</CardTitle>
                <CardDescription>
                  NZ tax treatment for different mining and exploration activities
                </CardDescription>
              </div>
              <Dialog open={treatmentDialogOpen} onOpenChange={setTreatmentDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Tax Treatment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Tax Treatment</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="category">Category</Label>
                        <Select 
                          value={newTaxTreatment.category}
                          onValueChange={(value) => setNewTaxTreatment({...newTaxTreatment, category: value as NZTaxTreatment["category"]})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exploration">Exploration</SelectItem>
                            <SelectItem value="development">Development</SelectItem>
                            <SelectItem value="production">Production</SelectItem>
                            <SelectItem value="rehabilitation">Rehabilitation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="expenseType">Expense Type</Label>
                        <Input 
                          id="expenseType" 
                          value={newTaxTreatment.expenseType || ''} 
                          onChange={(e) => setNewTaxTreatment({...newTaxTreatment, expenseType: e.target.value})} 
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="deductible">Deductible</Label>
                        <Input 
                          id="deductible" 
                          type="checkbox" 
                          className="w-4 h-4"
                          checked={newTaxTreatment.deductible}
                          onChange={(e) => setNewTaxTreatment({...newTaxTreatment, deductible: e.target.checked})}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="depreciable">Depreciable</Label>
                        <Input 
                          id="depreciable" 
                          type="checkbox"
                          className="w-4 h-4"
                          checked={newTaxTreatment.depreciable}
                          onChange={(e) => setNewTaxTreatment({...newTaxTreatment, depreciable: e.target.checked})}
                        />
                      </div>
                    </div>
                    {newTaxTreatment.depreciable && (
                      <div>
                        <Label htmlFor="depreciationRate">Depreciation Rate (%)</Label>
                        <Input 
                          id="depreciationRate" 
                          type="number"
                          step="0.1"
                          value={newTaxTreatment.depreciationRate || ''} 
                          onChange={(e) => setNewTaxTreatment({...newTaxTreatment, depreciationRate: parseFloat(e.target.value)})} 
                        />
                      </div>
                    )}
                    <div>
                      <Label htmlFor="applicableLegislation">Applicable Legislation</Label>
                      <Input 
                        id="applicableLegislation" 
                        value={newTaxTreatment.applicableLegislation || ''} 
                        onChange={(e) => setNewTaxTreatment({...newTaxTreatment, applicableLegislation: e.target.value})} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea 
                        id="notes" 
                        value={newTaxTreatment.notes || ''} 
                        onChange={(e) => setNewTaxTreatment({...newTaxTreatment, notes: e.target.value})} 
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleAddTaxTreatment}>Add Treatment</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Expense Type</TableHead>
                      <TableHead>Deductible</TableHead>
                      <TableHead>Depreciable</TableHead>
                      <TableHead>Depreciation Rate</TableHead>
                      <TableHead>Legislation Reference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxTreatments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No tax treatments added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      taxTreatments.map((treatment) => (
                        <TableRow key={treatment.id}>
                          <TableCell className="capitalize">{treatment.category}</TableCell>
                          <TableCell>{treatment.expenseType}</TableCell>
                          <TableCell>{treatment.deductible ? "Yes" : "No"}</TableCell>
                          <TableCell>{treatment.depreciable ? "Yes" : "No"}</TableCell>
                          <TableCell>{treatment.depreciationRate ? `${treatment.depreciationRate}%` : "N/A"}</TableCell>
                          <TableCell className="text-xs">{treatment.applicableLegislation || "Not specified"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Liabilities Tab */}
        <TabsContent value="liabilities">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Tax Liabilities by Year</CardTitle>
                <CardDescription>
                  Current and projected tax liabilities for your mining operations
                </CardDescription>
              </div>
              <Dialog open={liabilityDialogOpen} onOpenChange={setLiabilityDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Calculator className="mr-2 h-4 w-4" />
                    Add Tax Liability
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Tax Liability</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="year">Year</Label>
                        <Input 
                          id="year" 
                          type="number"
                          value={newTaxLiability.year || ''} 
                          onChange={(e) => setNewTaxLiability({...newTaxLiability, year: parseInt(e.target.value)})} 
                        />
                      </div>
                      <div>
                        <Label htmlFor="taxRate">Tax Rate (%)</Label>
                        <Input 
                          id="taxRate" 
                          type="number"
                          step="0.1"
                          value={newTaxLiability.taxRate || ''} 
                          onChange={(e) => setNewTaxLiability({...newTaxLiability, taxRate: parseFloat(e.target.value)})} 
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="taxableIncome">Taxable Income (NZD)</Label>
                      <Input 
                        id="taxableIncome" 
                        type="number"
                        value={newTaxLiability.taxableIncome || ''} 
                        onChange={(e) => setNewTaxLiability({...newTaxLiability, taxableIncome: parseFloat(e.target.value)})} 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="taxPayable">Tax Payable (NZD)</Label>
                        <Input 
                          id="taxPayable" 
                          type="number"
                          value={newTaxLiability.taxPayable || ''} 
                          onChange={(e) => setNewTaxLiability({...newTaxLiability, taxPayable: parseFloat(e.target.value)})} 
                        />
                      </div>
                      <div>
                        <Label htmlFor="taxCredits">Tax Credits (NZD)</Label>
                        <Input 
                          id="taxCredits" 
                          type="number"
                          value={newTaxLiability.taxCredits || ''} 
                          onChange={(e) => setNewTaxLiability({...newTaxLiability, taxCredits: parseFloat(e.target.value)})} 
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="netTaxLiability">Net Tax Liability (NZD)</Label>
                      <Input 
                        id="netTaxLiability" 
                        type="number"
                        value={newTaxLiability.netTaxLiability || ''} 
                        onChange={(e) => setNewTaxLiability({...newTaxLiability, netTaxLiability: parseFloat(e.target.value)})} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="liabilityNotes">Notes</Label>
                      <Textarea 
                        id="liabilityNotes" 
                        value={newTaxLiability.notes || ''} 
                        onChange={(e) => setNewTaxLiability({...newTaxLiability, notes: e.target.value})} 
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleAddTaxLiability}>Add Liability</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Taxable Income</TableHead>
                      <TableHead>Tax Rate</TableHead>
                      <TableHead>Tax Payable</TableHead>
                      <TableHead>Tax Credits</TableHead>
                      <TableHead>Net Liability</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxLiabilities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No tax liabilities added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      taxLiabilities.map((liability) => (
                        <TableRow key={liability.id}>
                          <TableCell>{liability.year}</TableCell>
                          <TableCell>${liability.taxableIncome.toLocaleString()}</TableCell>
                          <TableCell>{liability.taxRate}%</TableCell>
                          <TableCell>${liability.taxPayable.toLocaleString()}</TableCell>
                          <TableCell>${liability.taxCredits.toLocaleString()}</TableCell>
                          <TableCell>${liability.netTaxLiability.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Benefits Tab */}
        <TabsContent value="benefits">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Available Tax Benefits</CardTitle>
                <CardDescription>
                  Tax benefits, incentives, and deductions applicable to mining operations in NZ
                </CardDescription>
              </div>
              <Dialog open={benefitDialogOpen} onOpenChange={setBenefitDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <FileText className="mr-2 h-4 w-4" />
                    Add Tax Benefit
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Tax Benefit</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div>
                      <Label htmlFor="benefitName">Benefit Name</Label>
                      <Input 
                        id="benefitName" 
                        value={newTaxBenefit.name || ''} 
                        onChange={(e) => setNewTaxBenefit({...newTaxBenefit, name: e.target.value})} 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="benefitType">Benefit Type</Label>
                        <Select 
                          value={newTaxBenefit.type}
                          onValueChange={(value) => setNewTaxBenefit({...newTaxBenefit, type: value as TaxBenefitItem["type"]})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="deduction">Deduction</SelectItem>
                            <SelectItem value="credit">Credit</SelectItem>
                            <SelectItem value="allowance">Allowance</SelectItem>
                            <SelectItem value="incentive">Incentive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="benefitValue">Value</Label>
                        <Input 
                          id="benefitValue" 
                          type="number"
                          step="0.01"
                          value={newTaxBenefit.value || ''} 
                          onChange={(e) => setNewTaxBenefit({...newTaxBenefit, value: parseFloat(e.target.value)})} 
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="applicableYears">Applicable Years (comma separated)</Label>
                      <Input 
                        id="applicableYears" 
                        placeholder="e.g., 2025,2026,2027"
                        value={newTaxBenefit.applicableYears?.join(',')} 
                        onChange={(e) => {
                          const years = e.target.value.split(',').map(year => parseInt(year.trim())).filter(year => !isNaN(year));
                          setNewTaxBenefit({...newTaxBenefit, applicableYears: years});
                        }} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="legislationReference">Legislation Reference</Label>
                      <Input 
                        id="legislationReference" 
                        value={newTaxBenefit.legislationReference || ''} 
                        onChange={(e) => setNewTaxBenefit({...newTaxBenefit, legislationReference: e.target.value})} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="requirements">Requirements</Label>
                      <Textarea 
                        id="requirements" 
                        value={newTaxBenefit.requirements || ''} 
                        onChange={(e) => setNewTaxBenefit({...newTaxBenefit, requirements: e.target.value})} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="benefitNotes">Notes</Label>
                      <Textarea 
                        id="benefitNotes" 
                        value={newTaxBenefit.notes || ''} 
                        onChange={(e) => setNewTaxBenefit({...newTaxBenefit, notes: e.target.value})} 
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleAddTaxBenefit}>Add Benefit</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Benefit Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Applicable Years</TableHead>
                      <TableHead>Legislation Reference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxBenefits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No tax benefits added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      taxBenefits.map((benefit) => (
                        <TableRow key={benefit.id}>
                          <TableCell>{benefit.name}</TableCell>
                          <TableCell className="capitalize">{benefit.type}</TableCell>
                          <TableCell>
                            {benefit.type === 'credit' || benefit.type === 'deduction' 
                              ? `${(benefit.value * 100).toFixed(0)}%` 
                              : `$${benefit.value.toLocaleString()}`}
                          </TableCell>
                          <TableCell>{benefit.applicableYears.join(', ')}</TableCell>
                          <TableCell className="text-xs">{benefit.legislationReference}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="bg-muted p-4 rounded-lg mt-6">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div>
            <h3 className="font-semibold">NZ Mining Tax Information</h3>
            <p className="text-sm text-muted-foreground">
              Mining operations in New Zealand are subject to specific tax rules under the Income Tax Act 2007. 
              Exploration expenditure is generally deductible in the year incurred, while development expenditure 
              is typically capitalized and depreciated over the life of the mine.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Consult with a tax professional for specific advice related to your mining operation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
